import React from 'react';
import QRCode from 'react-qr-code';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { getBasePath } from 'js/utils/general';
import { generateShareCard } from 'js/utils/qrCard';

import { useSelector, useDispatch } from 'react-redux';

// import actions
import { getCollectionDataAndTypeById } from 'js/store/collections/Collections.selectors';
import { notificationPrompt } from 'js/store/notification/notification.actions';

// imoort selectors
import { getCardById } from 'js/store/cards/Cards.selectors';
import { getTranslationById } from 'js/store/app/App.selectors';
import { getLocale } from 'js/store/app/App.selectors';

// import components
import Thumbnail from 'js/components/thumbnail/Thumbnail';
import ButtonWithIcon from 'js/components/button/Button.with-icon';
import { getSkillTagTranslation } from '../../../store/app/App.selectors';

const CollectionShare = ({ collectionID }) => {
  const locale = useSelector((state) => getLocale(state));

  const dispatch = useDispatch();
  const siteText = {
    shareThisGallery: useSelector((state) =>
      getTranslationById(state, 'overlay_popup_share_this_gallery', 'Share this gallery')
    ),
    shareThisCollection: useSelector((state) =>
      getTranslationById(state, 'overlay_popup_share_this_collection', 'Share this collection')
    ),
    shareInfoText1: useSelector((state) =>
      getTranslationById(
        state,
        'overlay_popup_share_info_text_1',
        'Share your gallery with friends, family, or fellow educators!'
      )
    ),
    shareInfoText2: useSelector((state) =>
      getTranslationById(
        state,
        'overlay_popup_share_info_text_2',
        'Anyone with the link can view your gallery, but only you can edit it.'
      )
    ),
    saveQrCode: useSelector((state) => getTranslationById(state, 'overlay_popup_save_qr_code', 'Save QR code')),
    copyLink: useSelector((state) => getTranslationById(state, 'overlay_popup_copy_link', 'Copy link')),
  };

  const data = useSelector((state) => getCollectionDataAndTypeById(state, collectionID));
  const { collection, type } = data || {};

  const isCurated = type === 'curated';

  if (!collection) {
    return null;
  }

  const localisedCuratedName = useSelector((state) => getSkillTagTranslation(state, collection.name));
  const localisedName = useSelector((state) => getTranslationById(state, collection.name, collection.name));

  const name = isCurated ? localisedCuratedName : localisedName;

  const firstCard = useSelector((state) => getCardById(state, collection.cards[0]));
  const { largeImage } = firstCard?.front || {};

  const url = isCurated
    ? `${window.location.origin}${getBasePath() === '/' ? '' : getBasePath()}/collections/${collectionID}`
    : createQrUrl(collection);

  return (
    <div className="collection-modal__share">
      <OverlayScrollbarsComponent defer className="custom-scrollbar" style={{ height: '100vh' }}>
        <h1 style={{ marginBottom: 35 }}>{isCurated ? siteText.shareThisCollection : siteText.shareThisGallery}</h1>
        <div className="collection-modal">
          <CollectionSlide key={collection.id} image={largeImage} name={name} />

          <div className="card-modal">
            <div
              style={{
                height: 'auto',
                margin: '0 auto',
                maxWidth: 190,
                width: '100%',
                padding: 15,
                background: 'white',
                borderRadius: 15,
              }}
            >
              <QRCode
                id={'qr-code'}
                size={256}
                style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                value={url}
                viewBox={`0 0 256 256`}
              />
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 10,
              marginTop: 30,
              justifyContent: 'center',
            }}
          >
            <ButtonWithIcon
              title={siteText.saveQrCode}
              iconName={'download'}
              style={{
                fontSize: 14,
              }}
              action={() => {
                downloadPng(name, largeImage, locale, () => {
                  dispatch(notificationPrompt({ type: 'QR_CODE_SAVED', additionalData: false }));
                });
              }}
            />
            <ButtonWithIcon
              title={siteText.copyLink}
              iconName={'link'}
              style={{
                fontSize: 14,
              }}
              action={() => {
                navigator.clipboard
                  .writeText(url)
                  .then(() => {
                    dispatch(notificationPrompt({ type: 'LINK_COPIED', additionalData: false }));
                  })
                  .catch((err) => {
                    console.error('Failed to copy the link: ', err);
                  });
              }}
            />
          </div>
          <div
            style={{
              borderRadius: '40px',
              border: '3px solid #D2DB43',
              padding: 25,
              width: '100%',
              maxWidth: 350,
              margin: '30px auto 0 auto',
              textAlign: 'left',
            }}
          >
            <h4 style={{ color: '#D2DB43', fontSize: 24 }}>{siteText.shareInfoText1}</h4>
            <p style={{ color: '#fff', lineHeight: 1.1, fontSize: 20 }}>{siteText.shareInfoText2}</p>
          </div>
        </div>
      </OverlayScrollbarsComponent>
    </div>
  );
};

const CollectionSlide = ({ image, name }) => {
  return (
    <div
      style={{
        position: 'relative',
        margin: '0 auto',
        marginBottom: 10,
        maxWidth: 300,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Thumbnail src={image} alt={name} caption={''} />
      <h3 style={{ marginTop: -20 }}>{name}</h3>
    </div>
  );
};

export default CollectionShare;

function createQrUrl(data) {
  const json = JSON.stringify(data);
  const encoded = encodeURIComponent(json);
  return `${window.location.origin}${getBasePath() === '/' ? '' : getBasePath()}/share?data=${encoded}`;
}

const downloadPng = (name, image, locale, cb) => {
  const svg = document.getElementById('qr-code');
  if (!svg) return;

  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svg);

  const svgBlob = new Blob([svgString], {
    type: 'image/svg+xml;charset=utf-8',
  });

  const url = URL.createObjectURL(svgBlob);

  generateShareCard({ name, image, qr: url, type: locale === 'es' ? 'Colección' : 'Collection', locale }, cb);
};
